import { createFileRoute, Link } from "@tanstack/react-router";
import { Sprout, Sparkles, Compass } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { useLang } from "@/hooks/use-language";

export const Route = createFileRoute("/vision")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/vision" },
      { title: "Vision & Message | الرؤية والرسالة — Jozori" },
      {
        name: "description",
        content:
          "Jozori's vision and message — a warm, family-centered world that helps children grow rooted in values, gently and one week at a time.",
      },
      { property: "og:title", content: "Vision & Message | الرؤية والرسالة" },
      {
        property: "og:description",
        content:
          "Roots before reach. Family at the center. Warmth as a method. The honest words that guide everything inside Jozori.",
      },

      { property: "og:image", content: "https://jozori.com/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://jozori.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/vision" }],
  }),
  component: VisionPage,
});

const COPY = {
  en: {
    eyebrow: "Vision & Message",
    title: "We're not building an app. We're planting roots.",
    lede: "A few honest words that guide every story, every illustration, and every gentle activity inside Jozori.",
    visionTag: "Our Vision",
    vision:
      "A generation of children who grow up emotionally rooted, confident in who they are, and proud of where they come from — supported by families who feel guided, never overwhelmed.",
    missionTag: "Our Message",
    missionParagraphs: [
      "At Jozori, our message is to plant in every child deep, lasting roots of values — roots that hold them steady through life's challenges and gently guide them toward goodness and balanced growth.",
      "We believe today's child needs more than academics. They need their feelings nurtured, their behavior gently shaped, and their self-awareness awakened — all in a way that feels real, simple, and warmly loved.",
      "We walk alongside the child week by week, honor what makes them unique, and help them see themselves through eyes of love and trust. In every story, every gentle activity, every conversation… we plant a seed.",
    ],
    pillarsTitle: "What guides us",
    pillars: [
      {
        icon: Sprout,
        t: "Roots, not pressure",
        d: "We design for depth, not drills. Growth here feels calm, never rushed.",
      },
      {
        icon: Compass,
        t: "Family at the center",
        d: "Parents aren't an audience. They're co-walkers in every experience we ship.",
      },
      {
        icon: Sparkles,
        t: "Warmth as a method",
        d: "Children grow most where they feel seen, safe, and gently loved.",
      },
    ],
    cta: "Meet the founder",
  },
  ar: {
    eyebrow: "الرؤية والرسالة",
    title: "نحن لا نبني تطبيقًا. نحن نزرع جذورًا.",
    lede: "كلماتٌ صادقة تقود كل قصة، وكل رسمة، وكل نشاط هادئ داخل «جذوري».",
    visionTag: "رؤيتنا",
    vision:
      "أن نصنع مساحة يشعر فيها الطفل العربي، أينما كان، أن لغته وقيمه ما زالت قريبة منه.",
    missionTag: "رسالتنا",
    missionParagraphs: [
      "أن نرافق كل طفل بهدوء، ونزرع فيه جذورًا دافئة من القيم والمشاعر والثقة بالنفس.",
      "أن نمنح العائلة لحظات صغيرة تبقى — حوارات، قصصًا، وذكريات تكبر مع الطفل.",
    ],
    pillarsTitle: "ما الذي يقودنا",
    pillars: [
      {
        icon: Sprout,
        t: "جذور لا ضغوط",
        d: "نُصمّم للعمق لا للتمارين. النمو هنا هادئ، لا مُستعجَل.",
      },
      {
        icon: Compass,
        t: "العائلة في القلب",
        d: "الأهل ليسوا جمهورًا، بل رفاقٌ في كل تجربة نُطلقها.",
      },
      {
        icon: Sparkles,
        t: "الدفء طريقنا",
        d: "الأطفال ينمون أكثر حيث يشعرون أنهم مرئيون، آمنون، محبوبون بلطف.",
      },
    ],
    cta: "تعرّف على المؤسس",
  },
} as const;


function VisionPage() {
  const { lang } = useLang();
  const t = COPY[lang];
  const isAr = lang === "ar";
  return (
    <SiteShell active="/vision">
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-primary/10"
        />
        <div className="relative mx-auto max-w-3xl px-4 py-20 md:py-28 text-center">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary">
            {t.eyebrow}
          </p>
          <h1
            className={`text-4xl md:text-6xl font-black leading-tight mb-6 ${
              isAr ? "font-display-ar" : "font-display-en"
            }`}
          >
            {t.title}
          </h1>
          <p className="text-lg text-foreground/70">{t.lede}</p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12 grid gap-6 md:grid-cols-2">
        <article className="rounded-3xl border border-clay/40 bg-gradient-to-br from-primary/10 to-transparent p-10 shadow-sm">
          <p className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-primary">
            {t.visionTag}
          </p>
          <p className={`text-xl md:text-2xl font-bold leading-relaxed ${isAr ? "font-display-ar text-right" : ""}`}>
            {t.vision}
          </p>
        </article>
        <article className="rounded-3xl border border-clay/40 bg-gradient-to-br from-accent/15 to-transparent p-10 shadow-sm">
          <p className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-accent-foreground">
            {t.missionTag}
          </p>
          <div className={`space-y-4 text-lg md:text-xl font-medium ${isAr ? "font-display-ar text-right leading-[2]" : "leading-relaxed"}`}>
            {t.missionParagraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </article>

      </section>

      <section className="mx-auto max-w-6xl px-4 py-16">
        <h2 className="text-center text-3xl md:text-4xl font-black mb-12">{t.pillarsTitle}</h2>
        <div className="grid gap-5 md:grid-cols-3">
          {t.pillars.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.t}
                className={`rounded-2xl border border-clay/40 bg-card p-7 hover:shadow-md transition ${
                  isAr ? "text-right" : ""
                }`}
              >
                <div className="mb-4 inline-flex size-12 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                  <Icon className="size-6" />
                </div>
                <h3 className="font-black text-xl mb-2">{p.t}</h3>
                <p className="text-foreground/70 leading-relaxed">{p.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      <div className="text-center pb-20">
        <Link
          to="/founder"
          className="inline-flex items-center rounded-full bg-primary px-7 py-3.5 font-black text-primary-foreground shadow hover:opacity-90"
        >
          {t.cta}
        </Link>
      </div>
    </SiteShell>
  );
}
